import React, { useEffect, useState, useMemo } from "react";
import {
    Table,
    Button,
    Container,
    Modal,
    ModalHeader,
    ModalBody,
    Card,
    CardBody,
    CardTitle,
 } from "reactstrap";
import UserForm from "./UserForm";
import Session from '../auth/Session';
import useSortableData from "./useSortableData";
import SortableHeader from "./SortableHeader";

const UserGrid = () => {
    const apiBaseUrl = "";

    const [users, setUsers] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [roles, setRoles] = useState([]);

    const sortGetters = useMemo(() => ({
        name: (u) => `${u.firstName || ""} ${u.lastName || ""}`.trim(),
        active: (u) => (u.active ? "Yes" : "No"),
    }), []);

    const { items: sortedUsers, sortConfig, requestSort } = useSortableData(
        users,
        { key: "email", direction: "asc", getters: sortGetters }
    );

    const getRoles = async () => {
        const token = await Session.getToken();
        const url = `${apiBaseUrl}/api/Admin/GetRolesAsync`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }
        });

        if (response.ok) {
            let data = await response.json();
            setRoles(data);
        }
    }

    const fetchUsers = async () => {
        const token = await Session.getToken();
        const response = await fetch(`${apiBaseUrl}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await response.json();
        setUsers(data);
    };

    useEffect(() => {
        fetchUsers();
        getRoles();
    }, []);

    const handleNew = () => {
        setEditingUser(null);
        setModalOpen(true);
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete this user?")) {
            const token = await Session.getToken();
            await fetch(`${apiBaseUrl}/api/users/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
            fetchUsers();
        }
    };

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle tag="h5">User Management</CardTitle>
                        <Button color="primary" onClick={handleNew}>+ New User</Button>
                    </div>

                    <Table bordered striped>
                        <thead>
                            <tr>
                                <SortableHeader label="Email" sortKey="email" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Phone" sortKey="mobilePhone" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Active" sortKey="active" sortConfig={sortConfig} onSort={requestSort} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedUsers.map(u => (
                                <tr key={u.id}>
                                    <td>{u.email}</td>
                                    <td>{`${u.firstName || ""} ${u.lastName || ""}`}</td>
                                    <td>{u.mobilePhone}</td>
                                    <td>{u.active ? "Yes" : "No"}</td>
                                    <td>
                                        <Button size="sm" color="info" onClick={() => handleEdit(u)}>Edit</Button>{" "}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
                        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                            {editingUser ? "Edit User" : "New User"}
                        </ModalHeader>
                        <ModalBody>
                            <UserForm
                                apiBaseUrl={apiBaseUrl}
                                user={editingUser}
                                roles={roles}
                                onSuccess={() => { setModalOpen(false); fetchUsers(); }}
                                onCancel={() => setModalOpen(false)}
                            />
                        </ModalBody>
                    </Modal>
                </CardBody>
            </Card>
        </Container>
    );
};

export default UserGrid;
